/**
 * @fileoverview Institution Tab component for Profile Settings
 * Allows users to view and manage their institution links
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Grid,
    Divider,
    Checkbox,
    FormControlLabel,
    Button,
    Chip,
    Alert
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import DownloadIcon from '@mui/icons-material/Download';
import {
    intl,
    fetchData,
    HTTP_STATUS,
    DmsButton,
    Notifications,
    LanguageBaseContext
} from "dmsbasereact";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { DmsInstitutionSelect } from "../../../components/DmsInstitutionSelect/DmsInstitutionSelect";
import { InstitutionSetupModal } from "../../../components/InstitutionSetupModal";
import { InstitutionDocumentsSection } from "./components/InstitutionDocumentsSection";
import { mapBackendTypeToFrontend } from "../../../constants/UserType";
import { InstitutionStatusEnum, InstitutionStatus } from "../../../constants/InstitutionStatus";
import { UserDocumentTypeEnum } from "../../../constants/UserDocumentRequirements";
import { formatCnpj, formatCep } from "../../../utils/formatters";
import { downloadFile } from "../../../utils/fileDownload";

/**
 * InstitutionTab - Tab component for managing user institution links
 * @param {Object} props - Component props
 * @param {string} props.userType - Type of user (doctor, pharmacist, institutional_responsible)
 */
export const InstitutionTab = ({ userType, userTypes = [] }) => {
    // Get current language to trigger re-render when translations load/change
    // eslint-disable-next-line no-unused-vars
    const { language } = useContext(LanguageBaseContext);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [linkedInstitutions, setLinkedInstitutions] = useState([]);
    const [selectedInstitutionPks, setSelectedInstitutionPks] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    // Map organization_branch_pk → institution.pk for document uploads
    const [orgBranchToInstitutionMap, setOrgBranchToInstitutionMap] = useState({});
    // Key to force refresh of DmsInstitutionSelect after creating new institution
    const [institutionSelectKey, setInstitutionSelectKey] = useState(0);
    // Track institutions created in this session for auto-approval
    const [createdInstitutionPks, setCreatedInstitutionPks] = useState([]);

    // Ref for document upload section
    const documentsRef = useRef(null);

    // Declaration states
    const [declarationStatus, setDeclarationStatus] = useState(null);
    const [loadingDeclaration, setLoadingDeclaration] = useState(false);
    const [submittingDeclaration, setSubmittingDeclaration] = useState(false);

    // Document states
    const [institutionDocuments, setInstitutionDocuments] = useState({});
    const [institutionNameMap, setInstitutionNameMap] = useState({});

    const { confirmSave } = useConfirmDialog();
    const isDoctor = userType === 'doctor';
    const isPharmacist = userType === 'pharmacist';
    const isInstitutionalResponsible = userType === 'institution_responsible';
    const requiresDocument = isPharmacist;

    // Map backend userType to frontend USER_TYPE_VALUES for DmsInstitutionSelect and InstitutionSetupModal
    const effectiveUserType = useMemo(() => {
        return mapBackendTypeToFrontend(userType);
    }, [userType]);

    // Map all backend user types to frontend values for DmsInstitutionSelect
    // This ensures multi-role users (e.g. pharmacist + institution_responsible) see all applicable institutions
    const effectiveUserTypes = useMemo(() => {
        if (!userTypes || userTypes.length === 0) return null;
        return userTypes.map(mapBackendTypeToFrontend).filter(Boolean);
    }, [userTypes]);

    const handleDownloadDocument = useCallback(async (filePk, filename) => {
        try {
            await downloadFile(filePk, filename);
        } catch (err) {
            Notifications.toast(
                intl.get("DocumentDownloadError").d("Error downloading document"),
                Notifications.TYPE_ERROR
            );
        }
    }, []);

    // Declaration functions
    const fetchDeclarationStatus = useCallback(async () => {
        if (!isDoctor) return;
        setLoadingDeclaration(true);
        try {
            const response = await fetchData('/doctor/institution_link_declaration', 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                const data = response.data?.data || response.data;
                // New format includes per-institution declarations
                setDeclarationStatus(data);
            }
        } catch (error) {
            console.error('Error fetching declaration status:', error);
        } finally {
            setLoadingDeclaration(false);
        }
    }, [isDoctor]);

    const fetchUserInstitutions = useCallback(async () => {
        try {
            const response = await fetchData("/get/organization_branch/by_person_pk", "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                const data = response.data?.data;
                if (data?.institutions) {
                    setLinkedInstitutions(data.institutions);
                    setSelectedInstitutionPks(data.institutions.map(i => i.organization_branch_pk));

                    // Build map: organization_branch_pk → institution.pk
                    const map = {};
                    data.institutions.forEach(inst => {
                        map[inst.organization_branch_pk] = inst.pk;
                    });
                    setOrgBranchToInstitutionMap(map);
                } else if (data?.institution) {
                    setLinkedInstitutions([data.institution]);
                    setSelectedInstitutionPks([data.institution.organization_branch_pk]);

                    // Build map for single institution
                    const map = {
                        [data.institution.organization_branch_pk]: data.institution.pk
                    };
                    setOrgBranchToInstitutionMap(map);
                } else {
                    setLinkedInstitutions([]);
                    setSelectedInstitutionPks([]);
                    setOrgBranchToInstitutionMap({});
                }
            } else {
                setLinkedInstitutions([]);
                setSelectedInstitutionPks([]);
                setOrgBranchToInstitutionMap({});
            }
        } catch (err) {
            setLinkedInstitutions([]);
            setSelectedInstitutionPks([]);
            setOrgBranchToInstitutionMap({});
        }
    }, []);

    const fetchInstitutionDocuments = useCallback(async () => {
        if (!requiresDocument) return;

        try {
            const response = await fetchData("/account/documents", "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                const documents = response.data?.data || [];
                
                // Accept valid institutional link proof document types
                const validLinkTypes = [
                    UserDocumentTypeEnum.INSTITUTION_LINK_PROOF,
                    UserDocumentTypeEnum.CRT_CERTIFICATE,
                    UserDocumentTypeEnum.EMPLOYMENT_CONTRACT,
                    UserDocumentTypeEnum.PROFESSIONAL_ID,
                    UserDocumentTypeEnum.RESPONSIBILITY_TERM,
                ];
                const institutionDocs = documents.filter(doc => validLinkTypes.includes(doc.document_type));

                // Create map: organization_branch_pk -> document (for frontend mapping)
                const docMap = {};
                institutionDocs.forEach(doc => {
                    // Use organization_branch_pk if available, fallback to institution_pk
                    const key = doc.organization_branch_pk || doc.institution_pk;
                    if (key) {
                        docMap[key] = doc;
                    }
                });
                setInstitutionDocuments(docMap);
            }
        } catch (err) {
            console.error("Error fetching institution documents:", err);
        }
    }, [requiresDocument]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await fetchUserInstitutions();
            await fetchInstitutionDocuments();
            setLoading(false);
        };
        loadData();
    }, [fetchUserInstitutions, fetchInstitutionDocuments]);

    const handleEdit = () => {
        setIsEditMode(true);
    };

    const handleSaveNewInstitution = useCallback(async (payload, institutionPk) => {
        try {
            const response = await fetchData('/institution', 'POST', true, payload);

            if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                const newInstitution = response.data?.data;
                // Close modal and trigger success callback
                setShowCreateModal(false);
                Notifications.toast(
                    intl.get("InstitutionCreatedSuccess").d("Institution created and linked successfully"),
                    Notifications.TYPE_SUCCESS
                );
                // Force refresh of DmsInstitutionSelect to show the new institution
                setInstitutionSelectKey(prev => prev + 1);
                await fetchUserInstitutions();
                // Refresh declaration status so the declaration box appears immediately for doctors
                if (isDoctor) {
                    await fetchDeclarationStatus();
                }
                // If in edit mode and we got the new institution data, add it to selected
                if (newInstitution?.organization_branch_pk) {
                    setSelectedInstitutionPks(prev => {
                        if (!prev.includes(newInstitution.organization_branch_pk)) {
                            return [...prev, newInstitution.organization_branch_pk];
                        }
                        return prev;
                    });
                    // Track created institution for auto-approval
                    setCreatedInstitutionPks(prev => {
                        if (!prev.includes(newInstitution.organization_branch_pk)) {
                            return [...prev, newInstitution.organization_branch_pk];
                        }
                        return prev;
                    });
                }
                // Return the result so the modal can do the auto-link
                return newInstitution;
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("ErrorSavingInstitution").d("Error saving institution"),
                    Notifications.TYPE_ERROR
                );
                return null;
            }
        } catch (error) {
            Notifications.toast(
                error?.response?.data?.detail || error?.data?.detail || intl.get("ErrorSavingInstitution").d("Error saving institution"),
                Notifications.TYPE_ERROR
            );
            return null;
        }
    }, [fetchUserInstitutions, fetchDeclarationStatus, isDoctor]);

    const handleCancel = () => {
        setIsEditMode(false);
        setSelectedInstitutionPks(linkedInstitutions.map(i => i.organization_branch_pk));
    };

    const handleAcceptDeclaration = async (organization_branch_pk, checkboxId) => {
        // Check if the checkbox for this specific institution is checked
        const checkbox = document.getElementById(checkboxId);
        if (!checkbox || !checkbox.checked) {
            Notifications.toast(
                intl.get("PleaseAcceptDeclaration").d("Please check the box to accept the declaration"),
                Notifications.TYPE_WARNING
            );
            return;
        }

        setSubmittingDeclaration(true);
        
        try {
            const response = await fetchData('/doctor/institution_link_declaration', 'POST', true, {
                organization_branch_pk
            });
            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                // Notify Layout to refresh declaration state and dismiss the banner
                window.dispatchEvent(new CustomEvent('declarationAccepted'));
                // Refresh declaration status to get updated data
                await fetchDeclarationStatus();
                // Uncheck the checkbox after successful submission
                checkbox.checked = false;
                Notifications.toast(
                    intl.get("DeclarationAcceptedSuccess").d("Declaration accepted successfully"),
                    Notifications.TYPE_SUCCESS
                );
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("DeclarationAcceptedError").d("Error accepting declaration"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (error) {
            console.error('Error accepting declaration:', error);
            Notifications.toast(
                intl.get("DeclarationAcceptedError").d("Error accepting declaration"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setSubmittingDeclaration(false);
        }
    };

    // Fetch declaration status when institutions are loaded (only for doctors with institutions)
    useEffect(() => {
        if (isDoctor && linkedInstitutions.length > 0 && !loading) {
            fetchDeclarationStatus();
        }
    }, [isDoctor, linkedInstitutions.length, loading, fetchDeclarationStatus]);

    const hasLinksWithoutDocuments = useMemo(() => {
        return requiresDocument && linkedInstitutions.some(
            inst => !institutionDocuments[inst.organization_branch_pk]
        );
    }, [linkedInstitutions, institutionDocuments, requiresDocument]);

    const handleInstitutionItemsLoaded = useCallback((items) => {
        // Convert items array to { value: label } map for names
        const nameMap = {};
        // Build map: organization_branch_pk → institution.pk
        const pkMap = {};

        items.forEach(item => {
            nameMap[item.value] = item.label;
            // Add to PK map if institutionPk is available
            if (item.institutionPk) {
                pkMap[item.value] = item.institutionPk;
            }
        });

        setInstitutionNameMap(nameMap);
        // Update orgBranchToInstitutionMap with newly loaded institutions
        setOrgBranchToInstitutionMap(prev => ({
            ...prev,  // Keep existing mappings
            ...pkMap  // Add new mappings from DmsInstitutionSelect
        }));
    }, []);

    const handleDocumentsChange = useCallback((documents) => {
        // Convert documents array to map: institution_pk -> document
        const docMap = {};
        documents.forEach(doc => {
            if (doc.institution_pk) {
                docMap[doc.institution_pk] = doc;
            }
        });
        setInstitutionDocuments(docMap);
    }, []);

    const handleSave = async () => {
        if (selectedInstitutionPks.length === 0) {
            Notifications.toast(
                intl.get("SelectInstitutionFirst").d("Please select at least one institution"),
                Notifications.TYPE_WARNING
            );
            return;
        }

        const confirmed = await confirmSave({
            title: 'ConfirmSave',
            titleDefault: 'Confirm Save',
            message: 'ConfirmInstitutionChange',
            messageDefault: intl.get('UpdateInstitutionLinks').d('Are you sure you want to update your institution links?')
        });

        if (!confirmed) {
            return;
        }

        setSaving(true);
        try {
            // Step 1: Upload pending documents (if any)
            if (requiresDocument && documentsRef.current) {
                try {
                    await documentsRef.current.uploadPendingFiles();
                } catch (uploadError) {
                    console.error("Failed to upload documents:", uploadError);
                    Notifications.toast(
                        intl.get("DocumentUploadError").d("Error uploading document"),
                        Notifications.TYPE_ERROR
                    );
                    setSaving(false);
                    return; // Stop if document upload fails
                }
            }

            // Step 2: Sync institution links (backend will validate documents)
            const payload = {
                organization_branch_pks: selectedInstitutionPks
            };
            // Include created institutions for auto-approval
            if (createdInstitutionPks.length > 0) {
                payload.created_institution_pks = createdInstitutionPks;
            }
            const response = await fetchData("/person/organization_branches/sync", "PUT", true, payload);

            if (response.status === HTTP_STATUS.OK_200) {
                Notifications.toast(
                    intl.get("InstitutionUpdatedSuccess").d("Institutions updated successfully"),
                    Notifications.TYPE_SUCCESS
                );
                await fetchUserInstitutions();
                await fetchInstitutionDocuments();
                await fetchDeclarationStatus();
                setIsEditMode(false);
                // Clear created institutions after successful sync
                setCreatedInstitutionPks([]);
            } else {
                Notifications.toast(
                    response.data?.detail || intl.get("InstitutionUpdateError").d("Error updating institutions"),
                    Notifications.TYPE_ERROR
                );
            }
        } catch (err) {
            Notifications.toast(
                intl.get("InstitutionUpdateError").d("Error updating institutions"),
                Notifications.TYPE_ERROR
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    const formatPhone = (phone) => {
        if (!phone) return '-';
        return phone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, "($1) $2-$3");
    };


    const renderInstitutionCard = (institution, index) => {
        const orgBranch = institution?.organization_branch;
        const address = orgBranch?.address;
        const document = institutionDocuments[institution.organization_branch_pk];

        // Find declaration status for this institution
        const declarationInfo = declarationStatus?.institutions?.find(
            inst => inst.organization_branch_pk === institution.organization_branch_pk
        );
        const isDeclarationAccepted = declarationInfo?.doctor_declaration_accepted === true;
        const checkboxId = `declaration-checkbox-${institution.organization_branch_pk}`;

        return (
            <Box
                key={institution.pk || index}
                sx={{
                    p: 2,
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                    display: 'flex',
                    gap: 2,
                    flexDirection: { xs: 'column', md: 'row' }
                }}
            >
                {/* Left side - Institution Information */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {orgBranch?.name || '-'}
                        </Typography>
                        {institution?.status != null && (
                            <Chip
                                icon={<BusinessIcon />}
                                variant="outlined"
                                label={InstitutionStatus.getText(institution.status)}
                                color={InstitutionStatus.getColor(institution.status)}
                                size="small"
                            />
                        )}
                        {
                            //institution?.user_account_status != null && institution.user_account_status_label && 
                        (
                            <Chip
                                icon={<PersonIcon />}
                                label={institution.user_account_status_label}
                                size="small"
                                variant="outlined"
                                color={
                                    institution.user_account_status === 5 ? "success"
                                    :institution.user_account_status === 6 ? "error"
                                    : institution.user_account_status === 7 ? "error"
                                    : "warning"
                                }
                            />
                        )}
                    </Box>

                    <Grid container spacing={2}>
                    {orgBranch?.cnpj && (
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                                {intl.get("CNPJ").d("CNPJ")}
                            </Typography>
                            <Typography variant="body2">
                                {formatCnpj(orgBranch.cnpj)}
                            </Typography>
                        </Grid>
                    )}

                    {orgBranch?.email_address && (
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get("Email").d("Email")}
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                {orgBranch.email_address}
                            </Typography>
                        </Grid>
                    )}

                    {orgBranch?.phone_number && (
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get("Phone").d("Phone")}
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                {formatPhone(orgBranch.phone_number)}
                            </Typography>
                        </Grid>
                    )}

                    {address && (
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOnIcon fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get("Address").d("Address")}
                                </Typography>
                            </Box>
                            <Typography variant="body2">
                                {address.address}{address.number ? `, ${address.number}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {address.city} - {address.state}, {formatCep(address.zip_code)}
                            </Typography>
                        </Grid>
                    )}

                    {requiresDocument && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            {document ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => handleDownloadDocument(document.file_pk)}
                                    >
                                        {intl.get("DownloadLinkProof").d("Download Link Proof")}
                                    </Button>
                                    <Chip
                                        label={intl.get("DocumentUploaded").d("Document uploaded")}
                                        color="success"
                                        size="small"
                                    />
                                </Box>
                            ) : (
                                <Chip
                                    label={intl.get("NoDocumentUploaded").d("No document uploaded")}
                                    color="warning"
                                    size="small"
                                />
                            )}
                        </Grid>
                    )}
                    </Grid>
                </Box>

                {/* Right side - Doctor Declaration Section */}
                {isDoctor && !loadingDeclaration && declarationInfo && (
                    <Box sx={{
                        width: { xs: '100%', md: '640px' },
                        flexShrink: 0,
                        p: 3,
                        bgcolor: isDeclarationAccepted ? 'success.lighter' : 'warning.lighter',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: isDeclarationAccepted ? 'success.main' : 'warning.main',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {isDeclarationAccepted ? (
                                <CheckCircleIcon color="success" />
                            ) : (
                                <WarningAmberIcon color="warning" />
                            )}
                            <Typography variant="subtitle1" fontWeight="medium" sx={{ flex: 1 }}>
                                {intl.get("InstitutionLinkDeclarationTitle").d("Institution Link Declaration")}
                            </Typography>
                        </Box>

                        <Chip
                            label={isDeclarationAccepted ? intl.get("Accepted").d("Accepted") : intl.get("Required").d("Required")}
                            color={isDeclarationAccepted ? "success" : "warning"}
                            size="medium"
                            sx={{ alignSelf: 'flex-start' }}
                        />

                        {isDeclarationAccepted ? (
                            declarationInfo.doctor_declaration_accepted_on && (
                                <Typography variant="body2" color="text.secondary">
                                    {intl.get("AcceptedOn").d("Accepted on")}: {new Date(declarationInfo.doctor_declaration_accepted_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                            )
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id={checkboxId}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            {intl.get("InstitutionLinkDeclaration").d(
                                                "I declare that the information about my institutional link is true and accurate."
                                            )}
                                        </Typography>
                                    }
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                    fullWidth
                                    onClick={() => handleAcceptDeclaration(institution.organization_branch_pk, checkboxId)}
                                    disabled={submittingDeclaration}
                                    startIcon={submittingDeclaration ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                >
                                    {submittingDeclaration
                                        ? intl.get("Submitting").d("Submitting...")
                                        : intl.get("AcceptDeclaration").d("Accept Declaration")
                                    }
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const renderViewMode = () => {
        const hasInstitution = linkedInstitutions.length > 0;
        // Allow edit for all user types (doctors, pharmacists, and institutional responsibles)
        const showEditButton = true;
        const showCreateButton = !hasInstitution;

        return (
            <Box>
                {/* Banner de aviso para vínculos sem documentos */}
                {hasLinksWithoutDocuments && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {intl.get("InstitutionLinksMissingDocuments").d(
                            "One or more of your institution links are missing proof documents. Please upload documents to ensure compliance."
                        )}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon color="primary" />
                        <Typography variant="h6">
                            {intl.get("MyInstitutions").d("My Institutions")}
                        </Typography>
                    </Box>
                    <DmsButton
                        icon={showCreateButton ? <AddIcon /> : <EditIcon />}
                        text={showCreateButton ? intl.get("LinkToInstitution").d("Link to Institution") : intl.get("Edit").d("Edit")}
                        variant={showCreateButton ? "primary" : "secondary"}
                        size="small"
                        onClick={handleEdit}
                    />
                </Box>

                {hasInstitution ? (
                    <Box>
                        {linkedInstitutions.map((institution, index) =>
                            renderInstitutionCard(institution, index)
                        )}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {intl.get("NoInstitutionLinked").d("You are not linked to any institution")}
                    </Typography>
                )}
            </Box>
        );
    };

    const renderEditMode = () => {

        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <BusinessIcon color="primary" />
                    <Typography variant="h6">
                        {intl.get("MyInstitutions").d("My Institutions")}
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {intl.get("SelectInstitutionsToLink").d("Select the institutions you are linked to:")}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <DmsInstitutionSelect
                            key={institutionSelectKey}
                            label={intl.get("Institutions").d("Institutions")}
                            value={selectedInstitutionPks}
                            onChange={(newValue) => {
                                setSelectedInstitutionPks(Array.isArray(newValue) ? newValue : []);
                            }}
                            userType={effectiveUserType}
                            userTypes={effectiveUserTypes}
                            multiple={true}
                            fullWidth
                            needsAuthentication={true}
                            onItemsLoaded={handleInstitutionItemsLoaded}
                            showCreateButton={true}
                            onCreateInstitution={() => setShowCreateModal(true)}
                        />
                    </Grid>

                    {/* Document upload section */}
                    {requiresDocument && selectedInstitutionPks.length > 0 && (
                        <Grid item xs={12}>
                            <InstitutionDocumentsSection
                                ref={documentsRef}
                                selectedInstitutions={selectedInstitutionPks}
                                institutionNameMap={institutionNameMap}
                                orgBranchToInstitutionMap={orgBranchToInstitutionMap}
                                onDocumentsChange={handleDocumentsChange}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <DmsButton
                                text={intl.get("Cancel").d("Cancel")}
                                variant="secondary"
                                onClick={handleCancel}
                                disabled={saving}
                            />
                            <DmsButton
                                text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                                onClick={handleSave}
                                disabled={selectedInstitutionPks.length === 0 || saving}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <>
            {isEditMode ? renderEditMode() : renderViewMode()}
            {showCreateModal && (
                <InstitutionSetupModal
                    isVisible={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSave={handleSaveNewInstitution}
                    mode="create"
                    linkToUser={true}
                    existingInstitutionPks={linkedInstitutions.map(i => i.organization_branch_pk)}
                    userType={effectiveUserType}
                />
            )}
        </>
    );
};
